import React from 'react';
import {
  ClipboardCheck,
  FlaskConical,
  GitBranch,
  Leaf,
  Microscope,
  Sprout,
  Wheat
} from 'lucide-react';

const equipo = [
  {
    nombre: 'Mateo Maitini',
    cargo: 'Presidente',
    imagen: '/images/mateo-maitini.png',
    descripcion: [
      'Estudiante de la carrera de Marketing y Publicidad.',
      'Asesor de marketing y organizador de tareas.',
      'Asistente de breeder en diversas compañías durante varias campañas.'
    ]
  },
  {
    nombre: 'María Eva González',
    cargo: 'Breeder',
    imagen: '/images/maria-eva-gonzalez.jpeg',
    descripcion: [
      'Ingeniera Agrónoma de la Universidad de Rosario, con posgrados en Mejoramiento Genético y Vegetal en la misma universidad y en Especialización en el Cultivo de Granos en la Universidad de Buenos Aires.',
      'Cuenta con amplia experiencia en mejoramiento genético de soja. Ocupó cargos de relevancia en esta área en empresas líderes del sector, como Nidera Semillas, Monsanto —con alcance en Argentina, Paraguay y Uruguay—, Bayer y BASF.'
    ]
  }
];

const servicios = [
  { texto: 'Selección de padres', Icono: Sprout },
  { texto: 'Cruzamientos', Icono: GitBranch },
  {
    texto: 'Avance de generaciones F1, F2, F3 y F4, según las necesidades del proyecto, en contraestación, mediante Speed Breeding o a campo.',
    Icono: Leaf
  },
  {
    texto: 'Ensayos, toma de datos y análisis de resultados, junto con AgTesting.',
    Icono: Microscope
  },
  { texto: 'Inscripción de variedades en RNC y RNPC', Icono: ClipboardCheck },
  { texto: 'Ensayos de desarrollo de variedades', Icono: FlaskConical },
  { texto: 'Núcleo de pureza', Icono: Wheat },
  { texto: 'Todas las etapas de producción', Icono: Sprout }
];

function QuienesSomos() {
  return (
    <section className="relative overflow-hidden bg-stone-50 px-4 py-20 sm:py-24">
      <div className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 rounded-full bg-emerald-100/70 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 -left-40 h-96 w-96 rounded-full bg-lime-100/60 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        <div id="que-hacemos" className="border-t border-emerald-100 pt-12 sm:pt-16">
          <div className="mx-auto mb-8 max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">Del plan al cultivo</p>
            <h3 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Qué hacemos<span className="text-emerald-600">.</span>
            </h3>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-600">
              Asesoramos y brindamos un servicio integral para el desarrollo de cultivares, acompañando cada proyecto
              desde la selección inicial hasta las etapas de producción.
            </p>
          </div>

          <ul className="grid gap-x-10 md:grid-cols-2">
            {servicios.map(({ texto, Icono }) => (
              <li
                key={texto}
                className="group flex min-h-24 items-start gap-4 border-t border-slate-200/80 py-5 transition-colors hover:border-emerald-300"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 transition-colors group-hover:bg-emerald-100">
                  <Icono aria-hidden="true" className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <span className="flex-1 pt-0.5 text-sm leading-6 text-slate-700 sm:text-base">{texto}</span>
              </li>
            ))}
          </ul>

          <p className="mt-6 border-l-2 border-emerald-300 pl-4 text-sm font-medium text-slate-600">
            Acompañamiento en todas las etapas del proceso.
          </p>
        </div>

        <div id="historia" className="scroll-mt-24 pt-20">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <h2 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Quiénes somos
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              Somos una empresa familiar dedicada al mejoramiento genético vegetal para generar nuevos cultivares.
              Creamos variedades de manera eficiente y amigable con el medio ambiente, con el objetivo de lograr
              un negocio sustentable.
            </p>
            <p className="mt-4 text-base leading-7 text-slate-600">
              Nuestro foco es el cliente: realizamos las etapas del proceso que sean necesarias para alcanzar
              los objetivos particulares de cada proyecto, con la ética profesional y el cuidado del medio
              ambiente como ejes de nuestro trabajo.
            </p>
          </div>

          <div className="mb-20">
            <div className="mx-auto mb-8 max-w-3xl text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">Las personas detrás del trabajo</p>
              <h3 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Nuestro equipo</h3>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-600">
                Profesionales con experiencia y compromiso para acompañar cada etapa del desarrollo de cultivares.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {equipo.map((integrante) => (
                <article
                  key={integrante.nombre}
                  className="group overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="grid h-full sm:grid-cols-[minmax(180px,0.8fr)_1.2fr]">
                    <div className="relative min-h-72 overflow-hidden bg-emerald-100 sm:min-h-full">
                      <img
                        src={integrante.imagen}
                        alt={`Retrato de ${integrante.nombre}`}
                        className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent sm:bg-gradient-to-r sm:from-transparent sm:to-slate-950/10" />
                    </div>
                    <div className="p-6 sm:p-8">
                      <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-800">
                        {integrante.cargo}
                      </span>
                      <h4 className="mt-4 text-2xl font-bold text-slate-900">{integrante.nombre}</h4>
                      <div className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
                        {integrante.descripcion.map((texto) => <p key={texto}>{texto}</p>)}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default QuienesSomos;
